import { CategoryLandingPageKey } from '@lambda/apis/src/category/types';
import { handleFreeReturn } from '@lambda/commons/helpers';
import settings from '@/settings';
import { t } from './helpers';

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

type FaqT = {
  [key in CategoryLandingPageKey]?: {
    question: string;
    answer: string;
  }[];
};

const isNA = ['reebelo-us', 'reebelo-ca'].includes(settings.store);

export const FAQ: FaqT = {
  baby: [
    {
      question:
        'What are certified preloved, overstock, and sustainably sourced baby products?',
      answer: `Certified preloved, open-box and sustainably sourced baby products are pre-owned items that are thoroughly checked, cleaned, inspected, and ${t(
        'sanitized',
      )} before being sold. They undergo rigorous testing to ensure they look and feel like new. Overstock baby items are unused products that are shipped directly from manufacturer. All certified preloved and overstock baby gear comes with substantial cost savings, convenience, and guaranteed quality.`,
    },
    {
      question: 'Why choose refurbished baby products?',
      answer:
        'Choosing certified preloved, overstock, and gently used baby products is a smart decision. You can take advantage of significant discounts, up to 70% off, on these items compared to brand-new counterparts sold at retail value. Preloved and used products are also eco-friendly, minimizing carbon footprint and wastefulness. \n Rest assured, you buy thoroughly tested and cleaned strollers, car seats, and other essentials that are fully functional and covered by warranty. ',
    },
    {
      question: 'What to look for in pre-owned baby products? ',
      answer:
        'Quality pre-owned baby products on Reebelo guarantee peace of mind. They are packaged with trust and reliability. When buying pre-owned and gently used items, ensure they are: <ul><li>Certified and sold through vetted resellers.</li><li>Thoroughly checked for quality, safety standards, and recall protocols.</li><li>Covered by warranty & can be returned if you change your mind.</li></ul>',
    },
    {
      question:
        'What are the carbon footprint savings of buying gently used or overstock items?',
      answer:
        'Buying gently used or overstock baby items generates carbon footprint savings in multiple ways. First, by reducing the demand for resource-intensive manufacturing which results in tons of carbon emissions. Second, it also helps reduce packaging waste leading to water conservation. Lastly, by giving a new life to used baby products, you help by keeping plastics and raw materials out of landfills.',
    },
    {
      question: 'What payment methods are available?',
      answer: `We accept all major credit and debit cards, along with Apple Pay. You can also pay over time with Paypal${
        settings.store === 'reebelo-au' ? '.' : ' and Klarna.'
      }`,
    },
    {
      question: `What does the ${
        isNA ? 3 : settings.warranty_month
      } month warranty cover?`,
      answer: `The ${
        isNA ? 3 : settings.warranty_month
      }-month warranty covers manufacturing and operations defects. If the product you purchase stops working as intended or has functional issues, you can get it repaired, or replaced if needed, for ${
        isNA ? 3 : settings.warranty_month
      } months. Please note: this warranty does not cover cosmetic damage.`,
    },
  ],
  'sports-fitness': [
    {
      question: `What is used and refurbished sports and fitness gear?`,
      answer: `Used and reconditioned sports and fitness products are pre-owned items that are thoroughly checked, professionally cleaned, inspected, and ${t(
        'sanitized',
      )} before being sold. They undergo rigorous testing to ensure they look and feel like new. All certified refurbished and used sports and fitness equipment comes with substantial cost savings, convenience, and guaranteed quality.`,
    },
    {
      question: `Why should you buy certified and used sports and fitness equipment?`,
      answer: `Choosing certified preloved, used, and reconditioned sports and fitness products is a smart decision. You can take advantage of significant discounts, up to 70% off, on these items compared to brand-new counterparts sold at retail value. Preloved and used sports products are also eco-friendly, minimizing carbon footprint and wastefulness. \n Rest assured, you buy thoroughly tested and cleaned clubs, putters, binoculars, and other equipment that is fully functional and covered by warranty.`,
    },
    {
      question: `What to look for in reconditioned and used sports and fitness gear?`,
      answer: `Before buying reconditioned and used sports and fitness gear, it's important to understand the buyer protection you get and the condition your goods come in. \n <b>Are they cleaned & ${t(
        'sanitized',
      )}?</b> Reebelo partners with vendors that ensure 100% sanitization and hygiene before the equipment is sold. We have an extensive 70+ quality checklist that is held to the highest industry standards for every product. \n <b>Are they covered by warranty?</b> Sports and fitness equipment is covered by ${
        isNA ? 3 : settings.warranty_month
      } months of warranty. So if your purchase stops working within this period, we'll be happy to repair or replace it free of cost. \n <b>Can they be returned?</b> Yes, you can return unused equipment within ${freeReturnDays} days of purchase.`,
    },
    {
      question: `What are the carbon footprint savings of buying gently used or refurbished sports gear?`,
      answer: `Buying used or reconditioned sports and fitness gear generates carbon footprint savings in multiple ways. First, by reducing the demand for resource-intensive manufacturing which results in tons of carbon emissions. Second, it also helps reduce packaging waste leading to water conservation. Lastly, by giving a new life to used clubs and other equipment, you help by keeping plastics, metals, and other raw materials out of landfills.`,
    },
    {
      question: `What payment methods are available?`,
      answer: `We accept all major credit and debit cards, along with Apple Pay. You can also pay over time with Paypal${
        settings.store === 'reebelo-au' ? '.' : ' and Klarna.'
      }`,
    },
    {
      question: `What does the ${
        isNA ? 1 : settings.warranty_month
      }-month warranty cover?`,
      answer: `The ${
        isNA ? 1 : settings.warranty_month
      }-month warranty covers manufacturing and operations defects. If the product you purchase stops working as intended or has functional issues, you can get it repaired, or replaced if needed, for ${
        isNA ? 1 : settings.warranty_month
      } month. Please note: this warranty does not cover cosmetic damage.`,
    },
  ],
  'tools-home-improvement': [
    {
      question: `What are used and reconditioned tools?`,
      answer: `Used and reconditioned tools are pre-owned items that are thoroughly checked, professionally cleaned, inspected, and ${t(
        'sanitized',
      )} before being sold. They undergo rigorous testing to ensure they look and feel like new. All certified refurbished and used tools come with substantial cost savings, convenience, and guaranteed quality.`,
    },
    {
      question: `Why should you buy certified and used power tools?`,
      answer: `Choosing certified preloved, used, and reconditioned power tools is a smart decision. You can take advantage of significant discounts, up to 70% off, on these items compared to brand-new counterparts sold at retail value. Preloved and used products are also eco-friendly, minimizing carbon footprint and wastefulness. \n Rest assured, you buy thoroughly tested and cleaned power saws, tool sets, wrenches, and other equipment that are fully functional and covered by warranty.`,
    },
    {
      question: `What to look for in reconditioned and used tools?`,
      answer: `Before buying reconditioned and used tools, it's important to understand buyer protection and the condition they come in. \n <b>Are they cleaned & ${t(
        'sanitized',
      )}?</b> Reebelo partners with vendors that ensure 100% sanitization and hygiene before the power tools are sold. We have an extensive 70+ quality checkpoint list that is held to the highest industry standards for every product. \n <b>Are they covered by warranty?</b> Power tools are covered by ${
        isNA ? 3 : settings.warranty_month
      } months of warranty. So if they stop working within this period, we'll be happy to repair or replace them free of cost. \n <b>Can they be returned?</b> Yes, you can return unused equipment within ${freeReturnDays} days of purchase.`,
    },
    {
      question: `What are the carbon footprint savings of buying gently used and reconditioned power tools?`,
      answer: `Buying used or reconditioned power tools generates carbon footprint savings in multiple ways. First, by reducing the demand for resource-intensive manufacturing which results in tons of carbon emissions. Second, it also helps reduce packaging waste leading to water conservation. Lastly, by giving a new life to used tools and other equipment, you help by keeping plastics and raw materials out of landfills.`,
    },
    {
      question: `What payment methods are available?`,
      answer: `We accept all major credit and debit cards, along with Apple Pay. You can also pay over time with Paypal${
        settings.store === 'reebelo-au' ? '.' : ' and Klarna.'
      }`,
    },
    {
      question: `What does the ${
        isNA ? 3 : settings.warranty_month
      }-month warranty cover?`,
      answer: `The ${
        isNA ? 3 : settings.warranty_month
      }-month warranty covers manufacturing and operations defects. If the product you purchase stops working as intended or has functional issues, you can get it repaired, or replaced if needed, for ${
        isNA ? 3 : settings.warranty_month
      } months. Please note: this warranty does not cover cosmetic damage.`,
    },
  ],
};
